import { normalizeWord, wordCells } from '../core/language';
import type { WordPack } from '../content/packs';
import type { Lexicon } from '../core/game';
import { optionalArtPack, optionalArtPacks } from '../content/art-packs';
import { ArtCleanupUnavailable, withUnusedArt, withUnusedArts } from './art-use';
import { chooseArtEvictions } from '../core/art-eviction';
import type { DownloadedScene } from '../content/scenes';

export function validateGenerated(value: unknown, lexicon: Lexicon): { title: string; words: string[] } {
  if (!value || typeof value !== 'object') throw new Error('Invalid theme');
  const data = value as { title?: unknown; words?: unknown };
  if (typeof data.title !== 'string' || data.title.length < 1 || data.title.length > 80 || !Array.isArray(data.words) || data.words.length > 60) throw new Error('Invalid theme schema');
  const words = [...new Set(data.words.filter((word): word is string => typeof word === 'string').map(word => normalizeWord(word, lexicon.language)))].filter(word => wordCells(word, lexicon.language) && lexicon.permitted.has(word) && !lexicon.blocked.has(word));
  if (words.length < 5) throw new Error('Theme has too few permitted words');
  return { title: data.title, words };
}

export async function generateTheme(topic: string, lexicon: Lexicon): Promise<WordPack & { title: string }> {
  if (topic.trim().length < 3 || topic.length > 80) throw new Error('Topic length must be 3–80 characters');
  const response = await fetch('/api/themes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ topic: topic.trim(), locale: lexicon.language.locale }), signal: AbortSignal.timeout(30000) });
  if (!response.ok) throw new Error('Theme provider unavailable');
  const theme = validateGenerated(await response.json(), lexicon);
  return { ...theme, id: `generated-${Date.now()}`, version: '1', nameKey: '', language: lexicon.language, accent: '#c5dda5', source: 'Optional server-generated theme; locally validated' };
}

const artCache = 'wordhuli-optional-art-v1';
// Ignore only the shipped prototype placeholder, not other verified legacy packs.
const obsoletePlaceholder = '4104bc5092e22075876c6a541a17d59a2b867f0a3d0c230a9ecefa02c8338b54';
const maximumArtBytes = 1024 * 1024;
const artPolicyPath = '/packs/storage-policy.json';
export const artBudgetOptions = [524288, 1048576, 2097152, 10485760, 104857600];
export class ArtBudgetError extends Error {}
interface ArtVariant { shape: 'portrait' | 'landscape'; offset: number; bytes: number; width: number; height: number; focal: [number, number] }
interface ArtManifest { version: string; path: string; bytes: number; sha256: string; width?: number; height?: number; variants?: ArtVariant[] }
const artType = (manifest: ArtManifest): string => manifest.path.endsWith('.pack') ? 'application/octet-stream' : manifest.path.endsWith('.webp') ? 'image/webp' : 'image/svg+xml';
const validDimensions = (width: unknown, height: unknown): boolean => Number.isSafeInteger(width) && Number.isSafeInteger(height)
  && (width as number) > 0 && (height as number) > 0 && (width as number) <= 4096 && (height as number) <= 4096 && (width as number) * (height as number) <= 8_294_400;
let artEpoch = 0, artDownload: AbortController | undefined;
let artWrites: Promise<void> = Promise.resolve();
function writeArt(operation: () => Promise<void>): Promise<void> {
  artWrites = artWrites.catch(() => {}).then(() => navigator.locks
    ? navigator.locks.request('wordhuli-optional-art-write', operation) : operation());
  return artWrites;
}
function manifestFrom(value: unknown, packId: string): ArtManifest {
  if (!value || typeof value !== 'object') throw new Error('Invalid art manifest');
  const manifest = value as ArtManifest;
  if (typeof manifest.version !== 'string' || !/^[a-zA-Z0-9._-]{1,64}$/.test(manifest.version)
    || !optionalArtPack(packId).paths.some(path => path === manifest.path) || !Number.isSafeInteger(manifest.bytes) || manifest.bytes < 1 || manifest.bytes > maximumArtBytes
    || typeof manifest.sha256 !== 'string' || !/^[a-f0-9]{64}$/.test(manifest.sha256)) throw new Error('Invalid art manifest');
  if (artType(manifest) === 'application/octet-stream') {
    if (!Array.isArray(manifest.variants) || manifest.variants.length !== 2) throw new Error('Invalid art variants');
    let offset = 0;
    for (const [index, shape] of ['portrait', 'landscape'].entries()) {
      const variant = manifest.variants[index];
      if (!variant || variant.shape !== shape || !Number.isSafeInteger(variant.offset) || variant.offset !== offset
        || !Number.isSafeInteger(variant.bytes) || variant.bytes < 1 || variant.bytes > maximumArtBytes
        || !validDimensions(variant.width, variant.height) || !Array.isArray(variant.focal) || variant.focal.length !== 2
        || !variant.focal.every(value => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1)) throw new Error('Invalid art variant');
      offset += variant.bytes;
    }
    if (offset !== manifest.bytes) throw new Error('Art variants do not cover the bundle');
  } else if (manifest.variants !== undefined || artType(manifest) === 'image/webp' && !validDimensions(manifest.width, manifest.height)) throw new Error('Invalid art dimensions');
  return manifest;
}
async function readBounded(response: Response, limit: number, progress?: (received: number) => void): Promise<ArrayBuffer> {
  if (!response.body) throw new Error('Empty asset');
  const reader = response.body.getReader(), chunks: Uint8Array[] = [];
  let total = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      total += value.byteLength;
      if (total > limit) { await reader.cancel(); throw new Error('Asset exceeds size limit'); }
      chunks.push(value);
      progress?.(total);
    }
  } finally { reader.releaseLock(); }
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
  return bytes.buffer;
}
async function verifyArt(response: Response, manifest: ArtManifest, progress?: (received: number) => void): Promise<ArrayBuffer> {
  const bytes = await readBounded(response, manifest.bytes, progress);
  if (bytes.byteLength !== manifest.bytes) throw new Error('Size mismatch');
  const hash = [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map(byte => byte.toString(16).padStart(2, '0')).join('');
  if (hash !== manifest.sha256) throw new Error('Integrity mismatch');
  const variants = manifest.variants ?? (artType(manifest) === 'image/webp' ? [{ offset: 0, bytes: manifest.bytes, width: manifest.width!, height: manifest.height! }] : []);
  for (const variant of variants) {
    const url = URL.createObjectURL(new Blob([bytes.slice(variant.offset, variant.offset + variant.bytes)], { type: 'image/webp' }));
    try {
      const image = new Image(); image.src = url; await image.decode();
      if (image.naturalWidth !== variant.width || image.naturalHeight !== variant.height) throw new Error('Image dimensions mismatch');
    } finally { URL.revokeObjectURL(url); }
  }
  return bytes;
}

async function artUsage(cache: Cache): Promise<Map<string, number>> {
  const entries = new Map<string, number>();
  for (const pack of optionalArtPacks) {
    const response = await cache.match(pack.cacheKey);
    if (response) entries.set(pack.cacheKey, (await readBounded(response, maximumArtBytes)).byteLength);
  }
  return entries;
}
async function artPolicy(cache: Cache): Promise<{ budget: number; automatic: boolean }> {
  const response = await cache.match(artPolicyPath);
  if (!response) return { budget: 104857600, automatic: false };
  const policy = JSON.parse(new TextDecoder().decode(await readBounded(response, 1024)));
  if (policy?.version !== 1 || !artBudgetOptions.includes(policy.budget) || policy.automatic !== undefined && typeof policy.automatic !== 'boolean') throw new Error('Invalid artwork storage policy');
  return { budget: policy.budget, automatic: policy.automatic ?? false };
}
async function withArtRoom<T>(cache: Cache, key: string, bytes: number, operation: (victims: string[]) => Promise<T>): Promise<T> {
  const entries = await artUsage(cache), used = [...entries.values()].reduce((sum, value) => sum + value, 0);
  const policy = await artPolicy(cache), needed = used - (entries.get(key) ?? 0) + bytes - policy.budget;
  if (needed <= 0) return operation([]);
  if (!policy.automatic) throw new ArtBudgetError('Optional artwork budget exceeded');
  const candidates: { id: string; bytes: number; lastUsed: number }[] = [];
  for (const pack of optionalArtPacks) {
    if (pack.cacheKey === key || !entries.has(pack.cacheKey)) continue;
    const response = await cache.match(pack.cacheKey);
    if (response?.headers.get('X-WordHuli-Retain') === '1') continue;
    const stamp = Number(response?.headers.get('X-WordHuli-Last-Used') ?? 0);
    candidates.push({ id: pack.id, bytes: entries.get(pack.cacheKey)!, lastUsed: Number.isSafeInteger(stamp) && stamp >= 0 ? stamp : 0 });
  }
  return withUnusedArts(candidates.map(item => item.id), async available => {
    const victims = chooseArtEvictions(candidates, available, needed);
    if (!victims) throw new ArtBudgetError('Protected or active artwork prevents making room');
    return operation(victims);
  });
}
export async function artStorageState(): Promise<{ used: number; budget: number; retained: string[]; automatic: boolean }> {
  if (!('caches' in window)) throw new Error('Artwork storage unavailable');
  const cache = await caches.open(artCache), entries = await artUsage(cache);
  const retained: string[] = [];
  for (const pack of optionalArtPacks) if ((await cache.match(pack.cacheKey))?.headers.get('X-WordHuli-Retain') === '1') retained.push(pack.id);
  return { used: [...entries.values()].reduce((sum, value) => sum + value, 0), ...await artPolicy(cache), retained };
}
export async function touchArtPack(packId: string): Promise<void> {
  const pack = optionalArtPack(packId);
  await writeArt(async () => {
    const cache = await caches.open(artCache), response = await cache.match(pack.cacheKey);
    if (!response) return;
    const headers = new Headers(response.headers); headers.set('X-WordHuli-Last-Used', String(Date.now()));
    await cache.put(pack.cacheKey, new Response(response.body, { headers }));
  });
}
export async function setArtAutomatic(automatic: boolean): Promise<void> {
  if (automatic && !navigator.locks) throw new ArtCleanupUnavailable('Safe automatic cleanup unavailable');
  await writeArt(async () => {
    const cache = await caches.open(artCache), policy = await artPolicy(cache);
    await cache.put(artPolicyPath, new Response(JSON.stringify({ version: 1, ...policy, automatic }), { headers: { 'Content-Type': 'application/json' } }));
  });
}
export async function setArtRetention(packId: string, retained: boolean): Promise<void> {
  const pack = optionalArtPack(packId);
  await writeArt(async () => {
    const cache = await caches.open(artCache), response = await cache.match(pack.cacheKey);
    if (!response) throw new Error('Download artwork before protecting it');
    const metadata = response.headers.get('X-WordHuli-Art'), legacy = metadata ? null : await cache.match(pack.manifest);
    const manifest = manifestFrom(metadata ? JSON.parse(metadata) : legacy ? await legacy.json() : null, packId);
    const bytes = await verifyArt(response, manifest), headers = new Headers(response.headers);
    headers.set('X-WordHuli-Art', JSON.stringify(manifest)); headers.set('X-WordHuli-Retain', retained ? '1' : '0');
    await cache.put(pack.cacheKey, new Response(bytes, { headers }));
  });
}
export async function clearUnretainedArt(protectedIds: () => readonly string[]): Promise<string[]> {
  artEpoch++; artDownload?.abort(); artDownload = undefined;
  const removed: string[] = [];
  await writeArt(async () => {
    const cache = await caches.open(artCache);
    for (const pack of optionalArtPacks) {
      await withUnusedArt(pack.id, async () => {
        const response = await cache.match(pack.cacheKey);
        if (!response || response.headers.get('X-WordHuli-Retain') === '1' || protectedIds().includes(pack.id)) return;
        if (await cache.delete(pack.cacheKey)) removed.push(pack.id);
        await cache.delete(pack.manifest);
      });
    }
  });
  return removed;
}
export async function setArtBudget(budget: number): Promise<void> {
  if (!artBudgetOptions.includes(budget) || !('caches' in window)) throw new Error('Invalid artwork budget');
  await writeArt(async () => {
    const cache = await caches.open(artCache), entries = await artUsage(cache);
    if ([...entries.values()].reduce((sum, value) => sum + value, 0) > budget) throw new ArtBudgetError('Remove artwork before lowering the limit');
    const previous = await artPolicy(cache).catch(() => ({ automatic: false }));
    await cache.put(artPolicyPath, new Response(JSON.stringify({ version: 1, budget, automatic: previous.automatic }), { headers: { 'Content-Type': 'application/json' } }));
  });
}

export interface ArtDownloadProgress { received: number; total: number; saving: boolean }
export function cancelArtDownload(): void { artEpoch++; artDownload?.abort(); }

export async function downloadArtPack(progress?: (state: ArtDownloadProgress) => void, packId = 'forest'): Promise<string[]> {
  const pack = optionalArtPack(packId);
  if (!('caches' in window) || !crypto.subtle) throw new Error('Secure content storage unavailable');
  artDownload?.abort();
  const controller = new AbortController(), epoch = ++artEpoch;
  let evicted: string[] = [];
  artDownload = controller;
  const timeout = setTimeout(() => controller.abort(new DOMException('Download timed out', 'TimeoutError')), 20000);
  const live = () => {
    if (controller.signal.aborted) throw controller.signal.reason;
    if (epoch !== artEpoch) throw new DOMException('Download cancelled', 'AbortError');
  };
  try {
    const manifestResponse = await fetch(pack.manifest, { cache: 'no-store', signal: controller.signal });
    if (!manifestResponse.ok) throw new Error('Manifest unavailable');
    const manifest = manifestFrom(JSON.parse(new TextDecoder().decode(await readBounded(manifestResponse, 4096))), packId);
    progress?.({ received: 0, total: manifest.bytes, saving: false });
    await withArtRoom(await caches.open(artCache), pack.cacheKey, manifest.bytes, async () => {});
    const response = await fetch(manifest.path, { cache: 'no-store', signal: controller.signal });
    if (!response.ok) throw new Error('Art unavailable');
    const bytes = await verifyArt(response, manifest, received => progress?.({ received, total: manifest.bytes, saving: false }));
    await writeArt(async () => {
      live();
      const cache = await caches.open(artCache); live();
      await withArtRoom(cache, pack.cacheKey, bytes.byteLength, async victims => {
        live(); progress?.({ received: manifest.bytes, total: manifest.bytes, saving: true });
        const previous = await cache.match(pack.cacheKey), retain = previous?.headers.get('X-WordHuli-Retain') === '1';
        const backups: { key: string; response: Response }[] = [];
        for (const id of victims) for (const key of [optionalArtPack(id).cacheKey, optionalArtPack(id).manifest]) {
          const response = await cache.match(key); if (response) backups.push({ key, response });
        }
        live();
        // Save validated incoming bytes before removing any older eligible image.
        await cache.put(pack.cacheKey, new Response(bytes, { headers: { 'Content-Type': artType(manifest), 'X-WordHuli-Art': JSON.stringify(manifest), 'X-WordHuli-Retain': retain ? '1' : '0', 'X-WordHuli-Last-Used': String(Date.now()) } }));
        try {
          for (const id of victims) { await cache.delete(optionalArtPack(id).cacheKey); await cache.delete(optionalArtPack(id).manifest); }
          evicted = victims;
        } catch (error) {
          // Cache Storage has no multi-entry transaction: restore saved responses.
          if (previous) await cache.put(pack.cacheKey, previous); else await cache.delete(pack.cacheKey);
          for (const backup of backups) await cache.put(backup.key, backup.response);
          throw error;
        }
      });
    });
    return evicted;
  } finally { clearTimeout(timeout); if (artDownload === controller) artDownload = undefined; }
}

export async function deleteArtPack(packId?: string): Promise<void> {
  const pack = packId === undefined ? undefined : optionalArtPack(packId);
  artEpoch++; artDownload?.abort(); artDownload = undefined;
  if ('caches' in window) await writeArt(async () => {
    if (!pack) { await caches.delete(artCache); return; }
    const cache = await caches.open(artCache);
    await cache.delete(pack.cacheKey); await cache.delete(pack.manifest);
  });
}

export async function cachedArt(packId = 'forest'): Promise<DownloadedScene | null> {
  const pack = optionalArtPack(packId);
  if (!('caches' in window) || !crypto.subtle) return null;
  const epoch = artEpoch, cache = await caches.open(artCache), response = await cache.match(pack.cacheKey);
  if (!response) return null;
  const urls: string[] = [];
  try {
    const metadata = response.headers.get('X-WordHuli-Art');
    const legacy = metadata ? null : await cache.match(pack.manifest);
    const manifest = manifestFrom(metadata ? JSON.parse(metadata) : legacy ? await legacy.json() : null, packId);
    if (manifest.sha256 === obsoletePlaceholder) return null;
    const bytes = await verifyArt(response, manifest);
    if (epoch !== artEpoch) return null;
    const image = (variant?: ArtVariant) => {
      const src = URL.createObjectURL(new Blob([variant ? bytes.slice(variant.offset, variant.offset + variant.bytes) : bytes], { type: variant ? 'image/webp' : artType(manifest) }));
      urls.push(src); return { src, focal: variant?.focal ?? [.5, .5] as [number, number] };
    };
    const portrait = image(manifest.variants?.[0]), landscape = manifest.variants ? image(manifest.variants[1]) : portrait;
    const sceneId = manifest.sha256 === '5a20d5b48e1dc8a5fbff02a0bda67081e955e5c3fb886b3a08bad1f6b220de01' ? 'forest-dawn'
      : manifest.sha256 === 'abe59fd0744a606cd6eb418ed5856ad92c0d92594ee8044eaf8d835f7a1bfaa6' ? 'ocean-cove' : undefined;
    return { bytes: bytes.byteLength, portrait, landscape, ...(sceneId ? { sceneId } : {}) };
  } catch { for (const url of urls) URL.revokeObjectURL(url); return null; /* Invalid optional content never blocks bundled play. */ }
}
